import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, getSessionId } from '@/lib/api';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton } from '@/components/ui/Skeleton';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function Favorites() {
  const { data, isLoading } = useQuery<{ success: boolean; data: any[] }>({
    queryKey: ['favorites'],
    queryFn: () => api.favorites.getAll(getSessionId()),
  });

  const favorites = data?.data || [];

  return (
    <motion.div initial="initial" animate="animate" className="max-w-4xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-3 mb-2">
          <Heart size={28} className="text-red-500 fill-red-500" />
          <h1 className="text-4xl font-bold">Favorites</h1>
        </div>
        <p className="text-surface-500 mb-8">Your saved quotes</p>
      </motion.div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <QuoteSkeleton key={i} />
          ))}
        </div>
      ) : favorites.length === 0 ? (
        <motion.div variants={fadeUp} className="text-center py-16">
          <Sparkles size={48} className="mx-auto text-surface-300 mb-4" />
          <p className="text-xl text-surface-500 mb-2">No favorites yet</p>
          <p className="text-surface-400 mb-6">Start exploring and save quotes you love</p>
          <Link to="/browse" className="btn-primary inline-flex">
            Browse Quotes
          </Link>
        </motion.div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {favorites.map((fav: any, index: number) => {
            const quote = fav.quote || fav;
            return (
              <motion.div
                key={quote.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/quote/${quote.id}`}>
                  <QuoteCard quote={quote} isFavorited={true} />
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
